// Fieldtime design tokens — single source of every design value.
const deepFreeze = (obj) => {
  Object.values(obj).forEach((value) => {
    if (value && typeof value === 'object') deepFreeze(value);
  });
  return Object.freeze(obj);
};

export const tokens = deepFreeze({
  fontFamily: "Inter, 'Segoe UI', Arial, sans-serif",
  fontWeights: { regular: 400, medium: 500, semibold: 600 },
  fontFeatureSettings: "'cv11'",

  neutrals: {
    textPrimary: '#212529',
    textBody: '#495057',
    textSecondary: '#6c757d',
    textDisabled: '#adb5bd',
    divider: '#dee2e6',
    borderLight: '#e9ecef',
    headerRow: '#f1f3f5',
    tile: '#f8f9fa',
    fieldBorder: '#ced4da',
    white: '#ffffff',
  },
  primary: {
    main: '#1976d2',
    dark: '#1565c0',
    tint: '#e3f2fd',
    border: '#90caf9',
  },
  tones: {
    success: { bg: '#e8f5e9', text: '#2e7d32', border: '#a5d6a7' },
    warning: { bg: '#fef3c7', text: '#92400e', border: '#fde68a' },
    error: { bg: '#fdecec', text: '#b91c1c', border: '#fecaca' },
    info: { bg: '#e3f2fd', text: '#1565c0', border: '#90caf9' },
    neutral: { bg: '#e9ecef', text: '#495057', border: 'transparent' },
    // Not required / skipped / no bid (standards §2.3). The only dashed tone.
    neutralOutline: {
      bg: '#ffffff',
      text: '#868e96',
      border: '#ced4da',
      borderStyle: 'dashed',
    },
  },
  // Dark sidebar surfaces (ticket #295). The legacy SideNav still uses its own
  // constants with the same values; #290 moves it onto these.
  sidebar: {
    bg: '#111827',
    activeBg: '#1f2937',
    hoverBg: '#374151',
    text: '#9ca3af',
    textLight: '#d1d5db',
    textActive: '#ffffff',
    blockBg: 'rgba(255, 255, 255, 0.04)',
    blockOpenBg: 'rgba(255, 255, 255, 0.10)',
    blockBorder: 'rgba(255, 255, 255, 0.12)',
    track: 'rgba(255, 255, 255, 0.06)',
    divider: 'rgba(255, 255, 255, 0.08)',
  },
  // Full-height panels that open beside the sidebar (ContextSelectPanel).
  overlay: {
    backdrop: 'rgba(17, 24, 39, 0.45)',
    panelShadow: '8px 0 32px rgba(17, 24, 39, 0.25)',
  },
  category: {
    field: {
      bg: '#e3f2fd',
      text: '#1565c0',
      border: '#90caf9',
      solid: '#1976d2',
    },
    shop: {
      bg: '#e0f2f1',
      text: '#00695c',
      border: '#80cbc4',
      solid: '#26a69a',
    },
  },
  series: {
    budget: '#adb5bd',
    planned: '#1976d2',
    earned: '#2e7d32',
    actual: '#ed6c02',
    field: '#1976d2',
    shop: '#26a69a',
  },
  type: {
    dialogTitle: { fontSize: 18, fontWeight: 600 },
    sectionTitle: { fontSize: 16, fontWeight: 500 },
    body: { fontSize: 14, fontWeight: 400 },
    bodyStrong: { fontSize: 14, fontWeight: 500 },
    tableCell: { fontSize: 13, fontWeight: 400 },
    tableHeader: {
      fontSize: 11,
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
    },
    fieldLabel: { fontSize: 12, fontWeight: 500 },
    sectionLabel: {
      fontSize: 11,
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
    },
    subtitle: { fontSize: 13, fontWeight: 400 },
    caption: { fontSize: 12, fontWeight: 400 },
    statValue: { fontSize: 16, fontWeight: 600 },
    kpiValue: { fontSize: 24, fontWeight: 600 },
    unit: { fontSize: 12, fontWeight: 400 },
  },
  radius: { control: 4, card: 8, pill: 12 },
  motion: { fast: 100, base: 150, panel: 200 },
});

export default tokens;
